'use client';

import { type ReactNode, createContext, useContext } from 'react';

import type { AppShellSignals } from './appShell';

/**
 * The shell signals as the SERVER saw them on this request, handed to the
 * client tree so the server render and the hydration render agree.
 *
 * Without a seed, `useIsIosAppUser` / `useIsAndroidAppUser` answer `false` for
 * the server render and the first client render (the app signal lives in the
 * URL and `localStorage`, and only the second is invisible to the server — but
 * `useSyncExternalStore` has one server snapshot for both passes). The real
 * answer lands right after hydration, so an app-only or app-suppressed control
 * paints the WEB variant first and corrects itself a frame later. On the
 * Android shell that correction is visible as a third social button appearing
 * and then being pulled.
 *
 * The seed closes that gap where the shell's own params are on the URL — its
 * entry URL and the social-login return, both of which carry `?appVersion=` and
 * `?appPlatform=`. It cannot close it everywhere: a later in-app navigation
 * that drops the params leaves the server with nothing to seed from, and those
 * surfaces keep the post-hydration behaviour.
 *
 * `null` (no provider) means "not seeded" and is the default, so every surface
 * that does not opt in behaves exactly as before.
 */
const AppShellSeedContext = createContext<AppShellSignals | null>(null);

export function AppShellSeedProvider({
  seed,
  children,
}: {
  seed: AppShellSignals;
  children: ReactNode;
}) {
  return (
    <AppShellSeedContext.Provider value={seed}>
      {children}
    </AppShellSeedContext.Provider>
  );
}

/** The server-seeded signals, or `null` when this tree has no seed. */
export function useAppShellSeed(): AppShellSignals | null {
  return useContext(AppShellSeedContext);
}
